import { FC, useMemo } from 'react'

import { HideShowButton, Fields } from '../../../components'

import { useSafetyCaseStatusesQuery, EntityState, useSafetyCaseRiskMatrixQuery, useTagsQuery } from '../../../services'
import { useModal } from '../../../hooks'
import { toOptions } from '../../../util'

import s from './safety-cases-filter.module.scss'
import { useProfile } from '../../../security'
import { TagCategory } from '../../../interfaces'

type SafetyCaseFilterProps = {
  searchFilter: {
    searchText: string
    filterType?: string
    statusIds: number[]
    searchTagIds: number[]
    riskLevels: number[]
    sortBy?: number
    isSortByAsc: boolean
  }
  setSearchFilter: (values: { [key: string]: any }) => void
}

const filterTypeOptions = [
  { value: 'status', label: 'Status' },
  { value: 'label', label: 'Label' },
  { value: 'risk', label: 'Risk' },
]

const sortByOptions = [
  { value: 1, label: 'Date' },
  { value: 2, label: 'Status' },
  { value: 3, label: 'Risk' },
]

const SafetyCaseFilter: FC<SafetyCaseFilterProps> = props => {
  const { searchFilter, setSearchFilter } = props
  const { airportId } = useProfile();

  const {
    isOpen: isFilterVisible,
    openModal: openFilter,
    closeModal: closeFilter,
  } = useModal({ isOpenByDefault: true })

  const isStatusesFilterEnabled = searchFilter.filterType === 'status'

  const isTagsFilterEnabled = searchFilter.filterType === 'label'

  const isRiskLevelFilterEnabled = searchFilter.filterType === 'risk'

  const statusesQuery = useSafetyCaseStatusesQuery(
    { activeOnly: true, entityState: EntityState.Open },
    { enabled: isStatusesFilterEnabled },
  )

  const statusesOptions = useMemo(() => toOptions(statusesQuery?.data), [statusesQuery?.data])

  const tagsQuery = useTagsQuery({ airportId }, { tagCategory: TagCategory.SafetyCase }, { enabled: isTagsFilterEnabled })

  const tagsOptions = useMemo(() => toOptions(tagsQuery?.data), [tagsQuery?.data])

  const riskLevelsQuery = useSafetyCaseRiskMatrixQuery(undefined, undefined)

  const riskLevelsOptions = useMemo(() => {
    return riskLevelsQuery?.data?.risks
      ? riskLevelsQuery.data.risks.map(risk => ({ value: risk.level, label: risk.label }))
      : []
  }, [riskLevelsQuery?.data])

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <HideShowButton onHide={closeFilter} onShow={openFilter} show={isFilterVisible} text="Filter" />

        <div className="d-flex align-items-center">
          <span className="pr-2">Sort by:</span>

          <Fields.Select
            componentProps={{
              placeholder: 'Enter Sort By',
              wrapperClassName: 'mb-0',
              options: sortByOptions,
              onChange: (sortBy: number) => setSearchFilter({ sortBy }),
              value: searchFilter.sortBy,
            }}
          />

          {searchFilter.sortBy && (
            <button
              className="btn btn-link min-w-60px pl-1"
              onClick={() => setSearchFilter({ isSortByAsc: !searchFilter.isSortByAsc })}
            >
              {searchFilter.isSortByAsc ? 'Desc' : 'Asc'}
            </button>
          )}
        </div>
      </div>

      {isFilterVisible && (
        <div className="mb-4">
          <Fields.SearchInput
            componentProps={{
              wrapperClassName: 'mb-2',
              placeholder: 'Enter Text Search',
              maxLength: 50,
              onChange: (searchText: string) => setSearchFilter({ searchText }),
              value: searchFilter.searchText,
            }}
          />

          <div className="d-flex justify-content-between mt-2 flex-grow-0">
            <div className={s['filter-type-select']}>
              <Fields.Select
                componentProps={{
                  placeholder: 'Enter Filter Type',
                  wrapperClassName: 'mb-0',
                  options: filterTypeOptions,
                  onChange: (filterType: string) => setSearchFilter({ filterType }),
                  value: searchFilter.filterType,
                }}
              />
            </div>

            <div className={s['filter-type-definition']}>
              {isStatusesFilterEnabled && (
                <Fields.Select
                  componentProps={{
                    placeholder: 'Enter Statuses',
                    wrapperClassName: 'mb-0',
                    options: statusesOptions,
                    onChange: (statusIds: number[]) => setSearchFilter({ statusIds }),
                    value: searchFilter.statusIds,
                    isMulti: true,
                  }}
                />
              )}

              {isTagsFilterEnabled && (
                <Fields.Select
                  componentProps={{
                    placeholder: 'Enter Labels',
                    wrapperClassName: 'mb-0',
                    options: tagsOptions,
                    onChange: (searchTagIds: number[]) => setSearchFilter({ searchTagIds }),
                    value: searchFilter.searchTagIds,
                    isMulti: true,
                  }}
                />
              )}

              {isRiskLevelFilterEnabled && (
                <Fields.Select
                  componentProps={{
                    placeholder: 'Enter Risk Levels',
                    wrapperClassName: 'mb-0',
                    options: riskLevelsOptions,
                    onChange: (riskLevels: number[]) => setSearchFilter({ riskLevels }),
                    value: searchFilter.riskLevels,
                    isMulti: true,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SafetyCaseFilter
